import { makeAutoObservable, runInAction } from 'mobx'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { errorCodeOf } from './meteringStore'
import { formatPrice, type CardContent, type PaywallContent } from '@/components/PlanCard'

/**
 * A B2C user's subscription, as the sidebar, the paywall and Settings see it.
 *
 * Without an active subscription a B2C user has no modules: Module2 shows the plans
 * instead of the workspace. The server is the one that refuses (SUBSCRIPTION_REQUIRED);
 * this store only decides what to show. For a B2B user `applies` is false and nothing
 * here is shown.
 */

export const SUBSCRIPTION_REQUIRED = 'SUBSCRIPTION_REQUIRED'

export const isSubscriptionRequired = (error: any): boolean => errorCodeOf(error) === SUBSCRIPTION_REQUIRED

const PLAN_FIELDS = `
  id
  name
  priceAmount
  currency
  interval
  renews
  includedSeconds
  moduleIds
  moduleNames
`

const GET_MY_SUBSCRIPTION = gql`
  query getMySubscription {
    getMySubscription {
      applies
      hasAccess
      availableModuleIds
      totalSeconds
      nextExpiry
      subscription {
        id
        planId
        status
        cancelAtPeriodEnd
        currentPeriodEnd
        endedAt
      }
      plan {
        ${PLAN_FIELDS}
      }
      unusable {
        moduleNames
        remainingSeconds
        expiresAt
      }
    }
  }
`

const GET_PLAN_OFFER = gql`
  query getPlanOffer {
    getPlanOffer {
      subscribable
      paywallContent
      plans {
        ${PLAN_FIELDS}
        card
      }
    }
  }
`

const CREATE_SUBSCRIPTION_CHECKOUT = gql`
  mutation createSubscriptionCheckout($planId: Int!) {
    createSubscriptionCheckout(planId: $planId) {
      url
    }
  }
`

const CONFIRM_SUBSCRIPTION = gql`
  mutation confirmSubscriptionCheckout($checkoutSessionId: String!) {
    confirmSubscriptionCheckout(checkoutSessionId: $checkoutSessionId) {
      id
      status
    }
  }
`

const CREATE_PORTAL_SESSION = gql`
  mutation createSubscriptionPortalSession {
    createSubscriptionPortalSession {
      url
    }
  }
`

export interface SubscriptionPlan {
  id: number
  name: string
  /** Minor units of `currency`, per `interval`. */
  priceAmount: number
  currency: string
  interval: 'month' | 'quarter' | 'year'
  /** False: a one-period pass that the server sets to end at its period end. */
  renews: boolean
  includedSeconds: number
  moduleIds: number[]
  moduleNames: string[]
  /** How the paywall draws it; only in the offer. */
  card?: CardContent
}

export interface MySubscription {
  applies: boolean
  hasAccess: boolean
  availableModuleIds: number[]
  totalSeconds: number
  nextExpiry: string | null
  subscription: {
    id: number
    planId: number
    status: string
    cancelAtPeriodEnd: boolean
    currentPeriodEnd: string | null
    endedAt: string | null
  } | null
  plan: SubscriptionPlan | null
  unusable: { moduleNames: string[]; remainingSeconds: number; expiresAt: string }[]
}

/** Where the paywall stands after a return from Stripe. */
export type ActivationState = 'idle' | 'activating' | 'stillPending' | 'cancelled' | 'error'

const CONFIRM_DELAYS_MS = [2000, 4000, 8000, 16000]

class SubscriptionStore {
  mine: MySubscription | null = null
  loaded = false

  plans: SubscriptionPlan[] = []
  subscribable = false
  plansLoading = false
  /** The organisation's paywall texts; null keeps the built-in ones. */
  paywallContent: PaywallContent | null = null

  /** Which plan's subscribe button is waiting for its redirect. */
  subscribingPlanId: number | null = null
  /** A refusal code from the server (PLAN_UNAVAILABLE, SUBSCRIPTION_EXISTS, ...). */
  error: string | null = null
  activation: ActivationState = 'idle'
  openingPortal = false

  private confirmTimers: ReturnType<typeof setTimeout>[] = []

  constructor() {
    makeAutoObservable<SubscriptionStore, 'confirmTimers'>(this, { confirmTimers: false }, { autoBind: true })
  }

  /** A B2C user who cannot use the app right now: show the plans. */
  get showPaywall(): boolean {
    return this.loaded && !!this.mine?.applies && !this.mine.hasAccess
  }

  get totalSeconds(): number {
    return this.mine?.totalSeconds ?? 0
  }

  async refresh(): Promise<void> {
    try {
      const response = await authorizedGraphQLRequest<{ getMySubscription: MySubscription }>(GET_MY_SUBSCRIPTION)
      runInAction(() => {
        this.mine = response.getMySubscription
        this.loaded = true
      })
      if (this.showPaywall) await this.loadPlans()
    } catch (error) {
      console.error('Failed to load the subscription:', error)
      runInAction(() => {
        this.loaded = true
      })
    }
  }

  async loadPlans(): Promise<void> {
    this.plansLoading = true
    try {
      const response = await authorizedGraphQLRequest<{ getPlanOffer: { plans: SubscriptionPlan[]; subscribable: boolean; paywallContent: PaywallContent | null } }>(GET_PLAN_OFFER)
      runInAction(() => {
        this.plans = response.getPlanOffer?.plans ?? []
        this.subscribable = !!response.getPlanOffer?.subscribable
        this.paywallContent = response.getPlanOffer?.paywallContent ?? null
      })
    } catch (error) {
      console.error('Failed to load plans:', error)
    } finally {
      runInAction(() => {
        this.plansLoading = false
      })
    }
  }

  /** Start a subscription checkout and hand the browser to Stripe. Only a plan id is sent. */
  async subscribe(planId: number): Promise<void> {
    this.subscribingPlanId = planId
    this.error = null
    try {
      const response = await authorizedGraphQLRequest<{ createSubscriptionCheckout: { url: string } }>(CREATE_SUBSCRIPTION_CHECKOUT, { planId })
      const url = response.createSubscriptionCheckout?.url
      if (!url) throw new Error('No checkout URL')
      window.location.assign(url)
    } catch (error) {
      console.error('Failed to start the subscription checkout:', error)
      runInAction(() => {
        this.error = errorCodeOf(error) ?? 'CHECKOUT_FAILED'
        this.subscribingPlanId = null
      })
    }
  }

  /**
   * Back from Stripe. Asks the server to confirm now and again after 2, 4, 8 and 16
   * seconds while the subscription is not active yet.
   */
  async confirmReturn(checkoutSessionId: string): Promise<void> {
    this.clearTimers()
    this.activation = 'activating'

    const attempt = async (): Promise<boolean> => {
      try {
        const response = await authorizedGraphQLRequest<{ confirmSubscriptionCheckout: { status: string } }>(CONFIRM_SUBSCRIPTION, { checkoutSessionId })
        const status = response.confirmSubscriptionCheckout?.status
        if (status === 'active' || status === 'past_due' || status === 'trialing') {
          await this.refresh()
          runInAction(() => {
            this.activation = 'idle'
          })
          return true
        }
        if (status === 'expired' || status === 'canceled' || status === 'incomplete_expired') {
          runInAction(() => {
            this.activation = 'error'
          })
          return true
        }
        return false
      } catch (error) {
        console.error('Failed to confirm the subscription:', error)
        runInAction(() => {
          this.activation = 'error'
        })
        return true
      }
    }

    if (await attempt()) return

    for (const delay of CONFIRM_DELAYS_MS) {
      const settled = await new Promise<boolean>((resolve) => {
        const timer = setTimeout(() => {
          attempt().then(resolve, () => resolve(true))
        }, delay)
        this.confirmTimers.push(timer)
      })
      if (settled) return
    }

    runInAction(() => {
      this.activation = 'stillPending'
    })
  }

  cancelledReturn() {
    this.clearTimers()
    this.activation = 'cancelled'
  }

  /** Stripe's customer portal: cancel, change card, invoices. */
  async openPortal(): Promise<void> {
    this.openingPortal = true
    try {
      const response = await authorizedGraphQLRequest<{ createSubscriptionPortalSession: { url: string } }>(CREATE_PORTAL_SESSION)
      const url = response.createSubscriptionPortalSession?.url
      if (!url) throw new Error('No portal URL')
      window.location.assign(url)
    } catch (error) {
      console.error('Failed to open the subscription portal:', error)
      runInAction(() => {
        this.openingPortal = false
        this.error = errorCodeOf(error) ?? 'PORTAL_FAILED'
      })
    }
  }

  private clearTimers() {
    for (const timer of this.confirmTimers) clearTimeout(timer)
    this.confirmTimers = []
  }
}

export const subscriptionStore = new SubscriptionStore()

/** A plan's price in its currency, per its interval, in the reader's language. */
export const formatPlanPrice: (plan: Pick<SubscriptionPlan, 'priceAmount' | 'currency'>, language: string) => string = formatPrice

/** A date the way the reader's language writes it, day and month (and year when not this year). */
export const formatDay = (iso: string | null | undefined, language: string): string => {
  if (!iso) return ''
  const date = new Date(iso)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }) }).format(date)
}
